import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '@services/api.service';
import { ChangeLogWithSpreadingEvents, SpreadingEventEntry } from '@models/change-log-entry';
import { CallTypeCategory} from '@utils/enums';

@Injectable({
  providedIn: 'root'
})
export class ChangeLogService {

  constructor(
    public _apiService: ApiService,
  ) { }

  loadSpreadDataChangeLogForCompany(companyId: number) {
    const data = {
      callTypeCategory: CallTypeCategory.Spread,
      companyId: companyId,
    };
    return this._apiService.send('POST', `/api/call-log/all`, data).pipe(
      map(data => data.response.objects));
  }

  /**
   * Load the line-item changes shown on the Change Log page's Spreading Events tab.
   *
   * Deliberately a second method rather than a flag on loadSpreadDataChangeLogForCompany:
   * `borrowerEvents` changes the response from a flat list to a single-element wrapper, and
   * the existing callers - the Borrower Events table and the legacy change-log modal - are
   * built on the flat list. Opting in here confines the wrapper to the one caller that wants it.
   *
   * The response carries the call logs too; this returns only the spreading half, since the
   * Borrower Events table already loads its own rows.
   *
   * Note the request flag and the response key are different words for different things: the
   * flag is `borrowerEvents`, while the rows it unlocks arrive under `spreadingEvents`.
   *
   * Rows arrive in the order the panel displays them - see DEFAULT_ORDERING in the backend's
   * read_borrower_events.py - so nothing here re-sorts them.
   *
   * @param companyId the borrower whose events to read
   */
  loadSpreadingEventsForCompany(companyId: number): Observable<SpreadingEventEntry[]> {
    const data = {
      callTypeCategory: CallTypeCategory.Spread,
      companyId: companyId,
      borrowerEvents: true,
    };
    return this._apiService.send('POST', `/api/call-log/all`, data).pipe(
      map(response => {
        const wrapper: ChangeLogWithSpreadingEvents = response.response.objects?.[0];
        return wrapper?.spreadingEvents ?? [];
      }));
  }

  loadSingleEntityAnalysisChangeLogForCompany(companyId: number, analysisId: number) {
    const data = {
      callTypeCategory: CallTypeCategory.SingleEntityAnalysis,
      companyId: companyId,
      analysisId: analysisId,
    };
    return this._apiService.send('POST', `/api/call-log/all`, data).pipe(
      map(data => data.response.objects));
  }

  loadMultiEntityAnalysisChangeLogForCompany(companyId: number, analysisId: number) {
    const data = {
      callTypeCategory: CallTypeCategory.MultiEntityAnalysis,
      companyId: companyId,
      analysisId: analysisId,
    };
    return this._apiService.send('POST', `/api/call-log/all`, data).pipe(
      map(data => data.response.objects));
  }
}
